import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { BackOfficeState, initialState } from '@state/back-office';
import * as usersActions from '@state/back-office/back-office.actions';

const reducer: ActionReducer<BackOfficeState, Action> = createReducer(
  initialState,

  on(
    usersActions.getRoles,
    (state): BackOfficeState => ({
      ...state,
      loading: true,
      errorMessage: null,
      roles: [],
    })
  ),
  on(
    usersActions.getRolesSuccess,
    (state, { roles }): BackOfficeState => ({
      ...state,
      loading: false,
      errorMessage: null,
      roles,
    })
  ),
  on(
    usersActions.getRolesError,
    (state, { errorMessage }): BackOfficeState => ({
      ...state,
      errorMessage,
      loading: false,
      roles: [],
    })
  ),

  on(
    usersActions.getLanguages,
    (state): BackOfficeState => ({
      ...state,
      loading: true,
      errorMessage: null,
      languages: [],
    })
  ),
  on(
    usersActions.getLanguagesSuccess,
    (state, { languages }): BackOfficeState => ({
      ...state,
      loading: false,
      errorMessage: null,
      languages,
    })
  ),
  on(
    usersActions.getLanguagesError,
    (state, { errorMessage }): BackOfficeState => ({
      ...state,
      errorMessage,
      loading: false,
      languages: [],
    })
  ),

  on(
    usersActions.getCompanies,
    (state): BackOfficeState => ({
      ...state,
      loading: true,
      errorMessage: null,
      companies: [],
    })
  ),
  on(
    usersActions.getCompaniesSuccess,
    (state, { companies }): BackOfficeState => ({
      ...state,
      loading: false,
      errorMessage: null,
      companies,
    })
  ),
  on(
    usersActions.getCompaniesError,
    (state, { errorMessage }): BackOfficeState => ({
      ...state,
      errorMessage,
      loading: false,
      companies: [],
    })
  ),

  on(
    usersActions.getEmployees,
    (state): BackOfficeState => ({
      ...state,
      loading: true,
      errorMessage: null,
      employees: [],
    })
  ),
  on(
    usersActions.getEmployeesSuccess,
    (state, { employees }): BackOfficeState => ({
      ...state,
      loading: false,
      errorMessage: null,
      employees,
    })
  ),
  on(
    usersActions.getEmployeesError,
    (state, { errorMessage }): BackOfficeState => ({
      ...state,
      errorMessage,
      loading: false,
      employees: [],
    })
  ),

  on(
    usersActions.getPartners,
    (state): BackOfficeState => ({
      ...state,
      loadingPartners: true,
      errorMessage: null,
      partners: [],
    })
  ),
  on(
    usersActions.getPartnersSuccess,
    (state, { partners }): BackOfficeState => ({
      ...state,
      loadingPartners: false,
      errorMessage: null,
      partners,
    })
  ),
  on(
    usersActions.getPartnersError,
    (state, { errorMessage }): BackOfficeState => ({
      ...state,
      errorMessage,
      loadingPartners: false,
      partners: [],
    })
  ),

  on(
    usersActions.setSelectedPartners,
    (state, { payload }): BackOfficeState => ({
      ...state,
      selectedPartners: payload.selectedPartners,
    })
  ),

  // ############
  // USERS
  // ############

  on(
    usersActions.getUsers,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: true,
      errorUsersMessage: null,
      loadedUsersData: [],
    })
  ),
  on(
    usersActions.getUsersSuccess,
    (state, { users }): BackOfficeState => ({
      ...state,
      loadingUsers: false,
      errorUsersMessage: null,
      loadedUsersData: users.data,
      loadedUsersPagination: users.pagination,
    })
  ),
  on(
    usersActions.getUsersError,
    (state, { errorUsersMessage }): BackOfficeState => ({
      ...state,
      errorUsersMessage,
      loadingUsers: false,
      loadedUsersData: [],
    })
  ),

  on(
    usersActions.usersActivateAccount,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: true,
    })
  ),
  on(
    usersActions.usersActivateAccountSuccess,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: false,
    })
  ),
  on(
    usersActions.usersActivateAccountError,
    (state, { errorUsersMessage }): BackOfficeState => ({
      ...state,
      errorUsersMessage,
      loadingUsers: false,
    })
  ),

  on(
    usersActions.usersDeactivateAccount,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: true,
    })
  ),
  on(
    usersActions.usersDeactivateAccountSuccess,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: false,
    })
  ),
  on(
    usersActions.usersDeactivateAccountError,
    (state, { errorUsersMessage }): BackOfficeState => ({
      ...state,
      errorUsersMessage,
      loadingUsers: false,
    })
  ),

  on(
    usersActions.usersRestoreAccount,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: true,
    })
  ),
  on(
    usersActions.usersRestoreAccountSuccess,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: false,
    })
  ),
  on(
    usersActions.usersRestoreAccountError,
    (state, { errorUsersMessage }): BackOfficeState => ({
      ...state,
      errorUsersMessage,
      loadingUsers: false,
    })
  ),

  on(
    usersActions.usersChangePassword,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: true,
    })
  ),
  on(
    usersActions.usersChangePasswordSuccess,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: false,
    })
  ),
  on(
    usersActions.usersChangePasswordError,
    (state, { errorUsersMessage }): BackOfficeState => ({
      ...state,
      errorUsersMessage,
      loadingUsers: false,
    })
  ),

  on(
    usersActions.usersSoftDelete,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: true,
    })
  ),
  on(
    usersActions.usersSoftDeleteSuccess,
    (state): BackOfficeState => ({
      ...state,
      loadingUsers: false,
    })
  ),
  on(
    usersActions.usersSoftDeleteError,
    (state, { errorUsersMessage }): BackOfficeState => ({
      ...state,
      errorUsersMessage,
      loadingUsers: false,
    })
  ),

  // ############
  // TRANSACTIONS
  // ############

  on(
    usersActions.getTransactions,
    (state): BackOfficeState => ({
      ...state,
      loadingTransactions: true,
      errorTransactionsMessage: null,
      loadedTransactionsData: [],
    })
  ),
  on(
    usersActions.getTransactionsSuccess,
    (state, { transactions }): BackOfficeState => ({
      ...state,
      loadingTransactions: false,
      errorTransactionsMessage: null,
      loadedTransactionsData: transactions.data,
      loadedTransactionsPagination: transactions.pagination,
    })
  ),
  on(
    usersActions.getTransactionsError,
    (state, { errorTransactionsMessage }): BackOfficeState => ({
      ...state,
      errorTransactionsMessage,
      loadingTransactions: false,
      loadedTransactionsData: [],
    })
  ),

  on(
    usersActions.transactionsDownloadPurchaseDocument,
    (state): BackOfficeState => ({
      ...state,
      downloadingPurchaseDocument: true,
      errorTransactionsMessage: null,
    })
  ),
  on(
    usersActions.transactionsDownloadPurchaseDocumentSuccess,
    (state): BackOfficeState => ({
      ...state,
      downloadingPurchaseDocument: false,
      errorTransactionsMessage: null,
    })
  ),
  on(
    usersActions.transactionsDownloadPurchaseDocumentError,
    (state, { errorTransactionsMessage }): BackOfficeState => ({
      ...state,
      downloadingPurchaseDocument: false,
      errorTransactionsMessage,
    })
  ),

  on(
    usersActions.transactionsGenerateReport,
    (state): BackOfficeState => ({
      ...state,
      generatingTransactionsReport: true,
    })
  ),
  on(
    usersActions.transactionsGenerateReportSuccess,
    (state): BackOfficeState => ({
      ...state,
      generatingTransactionsReport: false,
    })
  ),
  on(
    usersActions.transactionsGenerateReportError,
    (state, { errorTransactionsMessage }): BackOfficeState => ({
      ...state,
      errorTransactionsMessage,
      generatingTransactionsReport: false,
    })
  ),

  // ############
  // APPLICATIONS
  // ############

  on(
    usersActions.getApplications,
    (state): BackOfficeState => ({
      ...state,
      loadingApplications: true,
      errorApplicationsMessage: null,
      loadedApplicationsData: [],
    })
  ),
  on(
    usersActions.getApplicationsSuccess,
    (state, { applications }): BackOfficeState => ({
      ...state,
      loadingApplications: false,
      errorApplicationsMessage: null,
      loadedApplicationsData: applications.data,
      loadedApplicationsPagination: applications.pagination,
    })
  ),
  on(
    usersActions.getApplicationsError,
    (state, { errorApplicationsMessage }): BackOfficeState => ({
      ...state,
      errorApplicationsMessage,
      loadingApplications: false,
      loadedApplicationsData: [],
    })
  ),

  on(
    usersActions.applicationsGenerateReport,
    (state): BackOfficeState => ({
      ...state,
      generatingApplicationReport: true,
    })
  ),
  on(
    usersActions.applicationsGenerateReportSuccess,
    (state): BackOfficeState => ({
      ...state,
      generatingApplicationReport: false,
    })
  ),
  on(
    usersActions.applicationsGenerateReportError,
    (state, { errorApplicationsMessage }): BackOfficeState => ({
      ...state,
      errorApplicationsMessage,
      generatingApplicationReport: false,
    })
  ),

  on(
    usersActions.applicationsSoftDelete,
    (state): BackOfficeState => ({
      ...state,
      loadingApplications: true,
    })
  ),
  on(
    usersActions.applicationsSoftDeleteSuccess,
    (state): BackOfficeState => ({
      ...state,
      loadingApplications: false,
    })
  ),
  on(
    usersActions.applicationsSoftDeleteError,
    (state, { errorApplicationsMessage }): BackOfficeState => ({
      ...state,
      errorApplicationsMessage,
      loadingApplications: false,
    })
  ),

  // ############
  // SCHEDULED FILES DELETION
  // ############

  on(
    usersActions.getScheduledFilesDeletion,
    (state): BackOfficeState => ({
      ...state,
      loadingScheduledFilesDeletion: true,
      errorScheduledFilesDeletionMessage: null,
      loadedScheduledFilesDeletionData: null,
    })
  ),
  on(
    usersActions.getScheduledFilesDeletionSuccess,
    (state, { scheduledFilesDeletion }): BackOfficeState => ({
      ...state,
      loadingScheduledFilesDeletion: false,
      errorScheduledFilesDeletionMessage: null,
      loadedScheduledFilesDeletionData: scheduledFilesDeletion.data,
      loadedScheduledFilesDeletionPagination: scheduledFilesDeletion.pagination,
    })
  ),
  on(
    usersActions.getScheduledFilesDeletionError,
    (state, { errorScheduledFilesDeletionMessage }): BackOfficeState => ({
      ...state,
      errorScheduledFilesDeletionMessage,
      loadingScheduledFilesDeletion: false,
      loadedScheduledFilesDeletionData: null,
    })
  ),

  // ############
  // MANAGE ASSETS
  // ############

  on(
    usersActions.getUserProcessManageFiles,
    (state): BackOfficeState => ({
      ...state,
      loadingManageFiles: true,
      errorManageFilesMessage: null,
      loadedUserProcessManageFiles: null,
    })
  ),
  on(
    usersActions.getUserProcessManageFilesSuccess,
    (state, { userProcessFiles }): BackOfficeState => ({
      ...state,
      loadingManageFiles: false,
      errorManageFilesMessage: null,
      loadedUserProcessManageFiles: userProcessFiles,
    })
  ),
  on(
    usersActions.getUserProcessManageFilesError,
    (state, { errorManageFilesMessage }): BackOfficeState => ({
      ...state,
      errorManageFilesMessage,
      loadingManageFiles: false,
      loadedUserProcessManageFiles: null,
    })
  )
);

export function backOfficeReducer(state: BackOfficeState, action: Action): BackOfficeState {
  return reducer(state, action);
}
